import { useCallback, useEffect, useRef, useState } from 'react';
import { cutTo as nextCut, isFresh, readCut } from '../engine/display/cut';
import { warnIfRejected } from '../diagnostics/log';

const REFUSED_HIDDEN = 'A hidden figure cannot be cut to: no player may see where it stands.';

/**
 * useCameraCut - the GM's cut (plan map-display, Track C): every display
 * camera to one figure, for one move, whatever each viewer's options say.
 *
 * `cut` is the newest FRESH cut (`engine/display/cut`): one this screen
 * finds waiting when it opens is old news and moves nothing. `cutTo(id)` is
 * the GM's: it writes `direction/cut` and moves the GM's own camera at once;
 * it is refused, with a sentence back, for a hidden figure - no player's
 * camera may point at one - and is a no-op off the GM's seat or out of a session.
 *
 * @param {object} o
 * @param {string|null} o.sessionId
 * @param {{ onCameraCutUpdate: Function, setCameraCut: Function }} o.mp
 * @param {boolean} o.isGm
 * @param {(id: string) => boolean} [o.isHidden]
 * @returns {{ cut: {id: string, seq: number}|null, cutTo: (id: string) => string|null }}
 */
export default function useCameraCut({ sessionId, mp, isGm, isHidden }) {
  const [cut, setCut] = useState(null);
  const last = useRef(null);
  useEffect(() => {
    if (!sessionId) return undefined;
    return mp.onCameraCutUpdate(sessionId, (raw) => {
      const read = readCut(raw);
      last.current = read;
      // The same cut heard twice is one move: the camera remembers which it has seen.
      if (read && isFresh(read, Date.now())) setCut(read);
    });
  }, [sessionId, mp]);
  const cutTo = useCallback(
    (id) => {
      if (!isGm || !sessionId) return null;
      if (isHidden?.(id)) return REFUSED_HIDDEN;
      const next = nextCut(id, last.current, Date.now());
      last.current = next;
      setCut(next);
      warnIfRejected(mp.setCameraCut(sessionId, next), 'display', 'camera cut write');
      return null;
    },
    [isGm, sessionId, isHidden, mp],
  );
  return { cut, cutTo };
}
